import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../shared/api";
import { NiceLineChart, Sparkline, WaterfallChart, Treemap, RankedBar, Heatmap, StackedAreaChart } from "../shared/charts";
import type { WaterfallBar } from "../shared/charts";

/* ── Design tokens ──────────────────────────────────────────────────────── */
const INK = "#1a202c";
const ACC = "#b1611e";
const ACC2 = "#8b6914";
const POS = "#1a7a32";
const NEG = "#b91c1c";
const BORDER = "#cbd5e0";
const BG = "#ffffff";
const BG_SUBTLE = "#f7fafc";
const BG_DARK = "#1a202c";
const GRAYS = ["#2d3748", "#4a5568", "#718096", "#a0aec0", "#cbd5e0", "#e2e8f0"];

/* ── Formatters ─────────────────────────────────────────────────────────── */
const nf = new Intl.NumberFormat("hr-HR", { maximumFractionDigits: 0 });
const nf2 = new Intl.NumberFormat("hr-HR", { maximumFractionDigits: 2 });
const eur = (n: number | null | undefined) => n == null ? "-" : `${nf.format(n)} \u20AC`;
const eur2 = (n: number | null | undefined) => n == null ? "-" : `${nf2.format(n)} \u20AC`;
const eurK = (n: number | null | undefined) => n == null ? "-" : n >= 1e6 ? `${nf2.format(n / 1e6)}M \u20AC` : n >= 1e3 ? `${nf.format(Math.round(n / 1e3))}k \u20AC` : eur(n);
const pct = (n: number | null | undefined) => n == null ? "-" : `${nf2.format(n)} %`;
const num = (n: number | null | undefined) => n == null ? "-" : nf.format(n);
const delta = (n: number | null | undefined) => n == null ? "-" : n > 0 ? `+${nf2.format(n)} %` : `${nf2.format(n)} %`;
const deltaTone = (n: number | null | undefined): string => n == null ? GRAYS[2] : n > 0 ? POS : n < 0 ? NEG : GRAYS[2];

/* ── Presets for date quick-pick ────────────────────────────────────────── */
const today = new Date();
const isoDate = (d: Date) => d.toISOString().slice(0, 10);
const startOfYear = (y: number) => `${y}-01-01`;
const endOfYear = (y: number) => `${y}-12-31`;
const thisYear = today.getFullYear();
const DATE_PRESETS: Array<{ label: string; from: string; to: string }> = [
    { label: `${thisYear} YTD`, from: startOfYear(thisYear), to: isoDate(today) },
    { label: `${thisYear} (cijela)`, from: startOfYear(thisYear), to: endOfYear(thisYear) },
    { label: `${thisYear - 1}`, from: startOfYear(thisYear - 1), to: endOfYear(thisYear - 1) },
    { label: "Zadnjih 90 dana", from: isoDate(new Date(today.getTime() - 90 * 864e5)), to: isoDate(today) },
    { label: "Zadnjih 180 dana", from: isoDate(new Date(today.getTime() - 180 * 864e5)), to: isoDate(today) },
    { label: "Zadnjih 365 dana", from: isoDate(new Date(today.getTime() - 365 * 864e5)), to: isoDate(today) },
    { label: `${thisYear - 1} + ${thisYear}`, from: startOfYear(thisYear - 1), to: isoDate(today) },
];

type Props = { token: string; locale?: string; onExit: () => void };
type ViewTab = "cockpit" | "pnl" | "costs" | "drilldown" | "branches" | "regions" | "trends";

const TABS: Array<{ key: ViewTab; label: string }> = [
    { key: "cockpit", label: "COCKPIT" },
    { key: "pnl", label: "P&L (DVOJNI)" },
    { key: "costs", label: "OPEX / CAPEX" },
    { key: "drilldown", label: "DRILLDOWN" },
    { key: "branches", label: "POSLOVNICE" },
    { key: "regions", label: "REGIJE" },
    { key: "trends", label: "TRENDOVI" },
];

/* ════════════════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                        */
/* ════════════════════════════════════════════════════════════════════════ */
export function AdoModule1c({ token, onExit }: Props) {
    const [view, setView] = useState<ViewTab>("cockpit");

    /* ── Date range state ─────────────────────────────────────────────── */
    const [dateFrom, setDateFrom] = useState<string>(startOfYear(thisYear));
    const [dateTo, setDateTo] = useState<string>(isoDate(today));
    const [showPresets, setShowPresets] = useState(false);
    const presetRef = useRef<HTMLDivElement>(null);

    /* ── Filters ──────────────────────────────────────────────────────── */
    const [region, setRegion] = useState<string>("all");
    const [branch, setBranch] = useState<string>("all");
    const [costType, setCostType] = useState<string>("ALL");
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [sortBy, setSortBy] = useState<string>("revenue_eur");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
    const [drillCat, setDrillCat] = useState<string>("");
    const [drillClass, setDrillClass] = useState<string>("");

    /* ── Data ─────────────────────────────────────────────────────────── */
    const [loading, setLoading] = useState(false);
    const [pnlData, setPnlData] = useState<any>(null);
    const [costsData, setCostsData] = useState<any>(null);
    const [drilldownData, setDrilldownData] = useState<any>(null);
    const [drillLoading, setDrillLoading] = useState(false);
    const [branchData, setBranchData] = useState<any>(null);
    const [trendData, setTrendData] = useState<any>(null);
    const abortRef = useRef<AbortController | null>(null);
    const drillAbortRef = useRef<AbortController | null>(null);

    /* Global query params - BEZ drillCat/drillClass jer drilldown ima svoj fetch */
    const queryParams = useMemo(() => {
        const p = new URLSearchParams();
        p.set("date_from", dateFrom);
        p.set("date_to", dateTo);
        if (region !== "all") p.set("region", region);
        if (branch !== "all") p.set("branch", branch);
        if (costType !== "ALL") p.set("type", costType);
        if (searchQuery.trim()) p.set("search", searchQuery.trim());
        p.set("sort_by", sortBy);
        p.set("sort_dir", sortDir);
        return p.toString();
    }, [dateFrom, dateTo, region, branch, costType, searchQuery, sortBy, sortDir]);

    /* Drilldown query params - globalni + kategorija */
    const drillQueryParams = useMemo(() => {
        const p = new URLSearchParams(queryParams);
        if (drillCat) p.set("category", drillCat);
        if (drillClass) p.set("classification", drillClass);
        return p.toString();
    }, [queryParams, drillCat, drillClass]);

    /* Globalni fetch - 4 endpointa, BEZ drilldowna */
    const loadData = useCallback(async (signal: AbortSignal) => {
        setLoading(true);
        try {
            const opts = { signal };
            const results = await Promise.allSettled([
                api<any>(`/ado-m1c/pnl?${queryParams}`, token, opts),
                api<any>(`/ado-m1c/costs?${queryParams}`, token, opts),
                api<any>(`/ado-m1c/branches?${queryParams}`, token, opts),
                api<any>(`/ado-m1c/trends?${queryParams}`, token, opts),
            ]);
            if (signal.aborted) return;
            const val = (r: PromiseSettledResult<any>) => r.status === "fulfilled" ? r.value : null;
            results.forEach((r, i) => { if (r.status === "rejected" && !signal.aborted) console.error(`M1c endpoint ${i} failed`, r.reason); });
            setPnlData(val(results[0]));
            setCostsData(val(results[1]));
            setBranchData(val(results[2]));
            setTrendData(val(results[3]));
        } catch (e: any) {
            if (e?.name !== "AbortError") console.error("M1c load error", e);
        } finally {
            if (!signal.aborted) setLoading(false);
        }
    }, [queryParams, token]);

    useEffect(() => {
        if (abortRef.current) abortRef.current.abort();
        const ac = new AbortController();
        abortRef.current = ac;
        loadData(ac.signal);
        return () => ac.abort();
    }, [loadData]);

    /* Zasebni fetch SAMO za drilldown - ne dira ostale podatke */
    const loadDrilldown = useCallback(async (signal: AbortSignal) => {
        setDrillLoading(true);
        try {
            const data = await api<any>(`/ado-m1c/drilldown?${drillQueryParams}`, token, { signal });
            if (!signal.aborted) setDrilldownData(data);
        } catch (e: any) {
            if (e?.name !== "AbortError") console.error("M1c drilldown error", e);
        } finally {
            if (!signal.aborted) setDrillLoading(false);
        }
    }, [drillQueryParams, token]);

    useEffect(() => {
        if (drillAbortRef.current) drillAbortRef.current.abort();
        const ac = new AbortController();
        drillAbortRef.current = ac;
        loadDrilldown(ac.signal);
        return () => ac.abort();
    }, [loadDrilldown]);

    /* Close preset dropdown on outside click */
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (presetRef.current && !presetRef.current.contains(e.target as Node)) setShowPresets(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleExport = (type: string) => {
        window.open(`/api/ado-m1c/export/${type}?${queryParams}`, "_blank");
    };

    const applyPreset = (from: string, to: string) => {
        setDateFrom(from);
        setDateTo(to);
        setShowPresets(false);
    };

    /* ── Derived cockpit KPIs ─────────────────────────────────────────── */
    const acc = pnlData?.accounting;
    const eco = pnlData?.economic;
    const rec = pnlData?.reconciliation;
    const tM = trendData?.monthly ?? [];

    /* ── Render ────────────────────────────────────────────────────────── */
    return (
        <div style={{ position: "fixed", inset: 0, zIndex: 4000, backgroundColor: BG, color: INK, fontFamily: "Arial, sans-serif", display: "flex", flexDirection: "column" }}>

            {/* ══════ HEADER ══════ */}
            <header style={{ borderBottom: `2px solid ${INK}`, backgroundColor: BG, padding: "10px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{ padding: "4px 10px", backgroundColor: INK, color: BG, fontWeight: 700, fontSize: 12, letterSpacing: "0.06em" }}>
                        MODUL 0.4
                    </div>
                    <h1 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
                        OTKUP I PRODAJA ZLATA - P&L, KONTROLING I POSLOVNICE
                    </h1>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <button onClick={() => handleExport(view === "pnl" ? "pnl" : view === "branches" ? "branches" : view === "costs" ? "costs" : "trends")} style={btnOutline}>IZVOZ (CSV)</button>
                    <button onClick={onExit} style={btnSolid}>ZATVORI</button>
                </div>
            </header>

            {/* ══════ FILTER BAR ══════ */}
            <div style={{ backgroundColor: BG_SUBTLE, borderBottom: `1px solid ${BORDER}`, padding: "8px 24px", display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center", fontSize: 12, flexShrink: 0 }}>
                {/* Date range */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, position: "relative" }} ref={presetRef}>
                    <span style={{ fontWeight: 700 }}>OD:</span>
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={inputSmall} />
                    <span style={{ fontWeight: 700 }}>DO:</span>
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={inputSmall} />
                    <button onClick={() => setShowPresets(!showPresets)} style={{ ...btnOutline, fontSize: 11, padding: "3px 8px" }}>
                        PRESET
                    </button>
                    {showPresets && (
                        <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, backgroundColor: BG, border: `1px solid ${BORDER}`, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 100, minWidth: 200, borderRadius: 4 }}>
                            {DATE_PRESETS.map((p) => (
                                <button key={p.label} onClick={() => applyPreset(p.from, p.to)} style={{ display: "block", width: "100%", textAlign: "left", padding: "7px 12px", border: "none", background: "transparent", fontSize: 12, cursor: "pointer", fontWeight: dateFrom === p.from && dateTo === p.to ? 700 : 400, color: dateFrom === p.from && dateTo === p.to ? ACC : INK }}>
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div style={{ width: 1, height: 20, backgroundColor: BORDER }} />

                {/* Region */}
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontWeight: 700 }}>REGIJA:</span>
                    <select value={region} onChange={(e) => { setRegion(e.target.value); setBranch("all"); }} style={selectSmall}>
                        <option value="all">Sve regije</option>
                        {(branchData?.regions ?? []).map((r: any) => (
                            <option key={r.code} value={r.code}>{r.name}</option>
                        ))}
                    </select>
                </div>

                {/* Branch */}
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontWeight: 700 }}>POSLOVNICA:</span>
                    <select value={branch} onChange={(e) => setBranch(e.target.value)} style={{ ...selectSmall, maxWidth: 240 }}>
                        <option value="all">Sve poslovnice</option>
                        {(branchData?.branches ?? []).map((b: any) => (
                            <option key={b.branch_code} value={b.branch_code}>{b.name}</option>
                        ))}
                    </select>
                </div>

                {loading && <span style={{ color: ACC, fontWeight: 700, marginLeft: "auto", fontSize: 11, letterSpacing: "0.04em" }}>UCITAVANJE...</span>}
            </div>

            {/* ══════ NAVIGATION TABS ══════ */}
            <nav style={{ display: "flex", borderBottom: `1px solid ${BORDER}`, backgroundColor: BG, padding: "0 24px", gap: 0, flexShrink: 0, overflowX: "auto" }}>
                {TABS.map((t) => {
                    const active = view === t.key;
                    return (
                        <button key={t.key} onClick={() => setView(t.key)} style={{
                            padding: "10px 18px", border: "none", borderBottom: active ? `3px solid ${ACC}` : "3px solid transparent",
                            backgroundColor: active ? BG_SUBTLE : "transparent", color: active ? INK : GRAYS[2],
                            fontWeight: active ? 700 : 600, fontSize: 11.5, cursor: "pointer", letterSpacing: "0.03em", whiteSpace: "nowrap",
                        }}>
                            {t.label}
                        </button>
                    );
                })}
            </nav>

            {/* ══════ MAIN CONTENT ══════ */}
            <main style={{ padding: 24, flex: 1, overflowY: "auto" }}>
                {view === "cockpit" && renderCockpitView(pnlData, costsData, trendData, branchData)}
                {view === "pnl" && renderPnlView(pnlData)}
                {view === "costs" && renderCostsView(costsData, costType, setCostType, drillCat, setDrillCat, drillClass, setDrillClass, drilldownData, drillLoading)}
                {view === "drilldown" && renderDrilldownView(drilldownData, searchQuery, setSearchQuery, drillLoading)}
                {view === "branches" && renderBranchesView(branchData, sortBy, sortDir, setSortBy, setSortDir)}
                {view === "regions" && renderRegionsView(branchData)}
                {view === "trends" && renderTrendsView(trendData)}
            </main>

            {/* ══════ FOOTER ══════ */}
            <footer style={{ borderTop: `1px solid ${BORDER}`, padding: "6px 24px", fontSize: 10, color: GRAYS[2], display: "flex", justifyContent: "space-between", backgroundColor: BG_SUBTLE, flexShrink: 0 }}>
                <span>Izvori: bi_ado_realizacija_lines, bi_lom_tca_calculations, bi_pnl_mng_map, bi_gl_journal_lines</span>
                <span>Modul 0.4 v1.0 | {dateFrom} - {dateTo}</span>
            </footer>
        </div>
    );
}

/* ── Shared inline styles ───────────────────────────────────────────────── */
const btnSolid: React.CSSProperties = { padding: "6px 14px", backgroundColor: INK, color: BG, border: `1px solid ${INK}`, fontWeight: 700, fontSize: 11, cursor: "pointer", letterSpacing: "0.03em" };
const btnOutline: React.CSSProperties = { padding: "6px 14px", backgroundColor: BG, color: INK, border: `1px solid ${INK}`, fontWeight: 700, fontSize: 11, cursor: "pointer" };
const inputSmall: React.CSSProperties = { padding: "4px 8px", border: `1px solid ${BORDER}`, backgroundColor: BG, fontSize: 12, width: 130 };
const selectSmall: React.CSSProperties = { padding: "4px 8px", border: `1px solid ${BORDER}`, backgroundColor: BG, fontSize: 12 };
const sectionTitle: React.CSSProperties = { backgroundColor: BG_SUBTLE, padding: "10px 16px", borderBottom: `1px solid ${BORDER}`, fontWeight: 700, fontSize: 13 };
const tblStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 12 };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "8px 12px", borderBottom: `2px solid ${INK}` };
const tdStyle: React.CSSProperties = { padding: "8px 12px", borderBottom: `1px solid ${BORDER}` };
const tdNum: React.CSSProperties = { ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" };

/* ════════════════════════════════════════════════════════════════════════ */
/*  COCKPIT TAB                                                            */
/* ════════════════════════════════════════════════════════════════════════ */
function renderCockpitView(pnlData: any, costsData: any, trendData: any, branchData: any) {
    if (!pnlData) return <p style={{ color: GRAYS[2] }}>Ucitavanje cockpita...</p>;
    const acc = pnlData.accounting;
    const eco = pnlData.economic;
    const rec = pnlData.reconciliation;
    const opex = acc?.opex;
    const monthly = trendData?.monthly ?? [];
    const branches = branchData?.branches ?? [];
    const cats = costsData?.categories ?? [];
    const yoy = trendData?.yoy_q1?.growth;

    /* Sparkline data for KPI cards */
    const revSpark = monthly.map((m: any) => m.revenue_eur ?? 0);
    const marginSpark = monthly.map((m: any) => m.gross_profit_eur ?? 0);
    const opexSpark = monthly.map((m: any) => m.opex_eur ?? 0);
    const netSpark = monthly.map((m: any) => m.net_profit_eur ?? 0);

    /* Waterfall bars for P&L summary */
    const wfBars: WaterfallBar[] = [
        { label: "Prihod", debit: acc?.totals?.revenue_eur ?? 0, credit: 0, net: acc?.totals?.revenue_eur ?? 0 },
        { label: "COGS", debit: 0, credit: Math.abs(acc?.totals?.cogs_eur ?? 0), net: -(acc?.totals?.cogs_eur ?? 0) },
        { label: "Bruto", debit: acc?.totals?.gross_profit_eur ?? 0, credit: 0, net: acc?.totals?.gross_profit_eur ?? 0 },
        { label: "OPEX", debit: 0, credit: Math.abs(opex?.total_opex_eur ?? 0), net: -(opex?.total_opex_eur ?? 0) },
        { label: "EBITDA", debit: acc?.ebitda?.operating_profit_eur ?? 0, credit: 0, net: acc?.ebitda?.operating_profit_eur ?? 0 },
        { label: "CAPEX", debit: 0, credit: Math.abs(acc?.capex?.total_capex_eur ?? 0), net: -(acc?.capex?.total_capex_eur ?? 0) },
        { label: "Neto", debit: pnlData?.net_after_capex_eur ?? 0, credit: 0, net: pnlData?.net_after_capex_eur ?? 0 },
    ];

    /* Top 5 cost categories for treemap */
    const topCats = [...cats].sort((a: any, b: any) => Math.abs(b.amount_eur) - Math.abs(a.amount_eur)).slice(0, 8);
    const treemapItems = topCats.map((c: any, i: number) => ({ label: c.name ?? c.code, value: Math.abs(c.amount_eur ?? 0), color: GRAYS[Math.min(i, GRAYS.length - 1)] }));

    /* Revenue trend chart data */
    const trendLabels = monthly.map((m: any) => m.period);
    const trendRevenue = monthly.map((m: any) => m.revenue_eur ?? 0);
    const trendMargin = monthly.map((m: any) => m.gross_profit_eur ?? 0);
    const trendNet = monthly.map((m: any) => m.net_profit_eur ?? null);

    /* Top 5 branches by revenue */
    const topBranches = [...branches].sort((a: any, b: any) => (b.revenue_eur ?? 0) - (a.revenue_eur ?? 0)).slice(0, 10);

    return (
        <div>
            {/* Row 1: Hero KPI cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
                <HeroKpi label="PRIHOD" value={eurK(acc?.totals?.revenue_eur)} delta={yoy?.revenue_growth_pct != null ? delta(yoy.revenue_growth_pct) : undefined} deltaToneColor={deltaTone(yoy?.revenue_growth_pct)} spark={revSpark} />
                <HeroKpi label="BRUTO MARZA" value={eurK(acc?.totals?.gross_profit_eur)} sub={pct(acc?.totals?.gross_margin_pct)} spark={marginSpark} accent />
                <HeroKpi label="OPERATIVNA DOBIT" value={eurK(acc?.ebitda?.operating_profit_eur)} sub={`OPEX: ${eurK(opex?.total_opex_eur)}`} spark={opexSpark} />
                <HeroKpi label="EKONOMSKA BRUTO MARZA" value={eurK(eco?.totals?.economic_gross_profit_eur)} sub={`EBITDA ekon.: ${eurK(eco?.totals?.economic_operating_profit_eur)}`} spark={netSpark} accent />
            </div>

            {/* Row 2: Waterfall + Trend chart */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                <div style={{ border: `1px solid ${BORDER}`, padding: 16, backgroundColor: BG }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>P&L WATERFALL (KNJIGOVODSTVENI)</div>
                    <WaterfallChart bars={wfBars} />
                </div>
                <div style={{ border: `1px solid ${BORDER}`, padding: 16, backgroundColor: BG }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>TREND PRIHODA I MARZE (EKONOMSKI)</div>
                    <NiceLineChart
                        series={[
                            { name: "Prihod", color: INK, values: trendRevenue },
                            { name: "Bruto marza", color: ACC, values: trendMargin },
                            { name: "Neto", color: GRAYS[2], values: trendNet, style: "dashed" },
                        ]}
                        xLabels={trendLabels}
                        height={220}
                    />
                </div>
            </div>

            {/* Row 3: Treemap costs + Top branches */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                <div style={{ border: `1px solid ${BORDER}`, padding: 16, backgroundColor: BG }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>STRUKTURA TROSKOVA (TOP 8)</div>
                    {treemapItems.length > 0 ? <Treemap items={treemapItems} height={200} /> : <p style={{ color: GRAYS[2] }}>Nema podataka.</p>}
                </div>
                <div style={{ border: `1px solid ${BORDER}`, padding: 16, backgroundColor: BG }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>TOP 10 POSLOVNICA (PRIHOD)</div>
                    {topBranches.length > 0 ? (
                        <RankedBar items={topBranches.map((b: any) => ({ label: b.name, value: b.revenue_eur ?? 0 }))} />
                    ) : <p style={{ color: GRAYS[2] }}>Nema podataka.</p>}
                </div>
            </div>

            {/* Row 4: YoY summary cards */}
            {yoy && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                    <MiniKpi label="Rast prodaje (YoY)" value={delta(yoy.revenue_growth_pct)} tone={deltaTone(yoy.revenue_growth_pct)} />
                    <MiniKpi label="Rast bruto marze (YoY)" value={delta(yoy.gross_profit_growth_pct)} tone={deltaTone(yoy.gross_profit_growth_pct)} />
                    <MiniKpi label="Rast broja racuna" value={delta(yoy.tx_growth_pct)} tone={deltaTone(yoy.tx_growth_pct)} />
                    <MiniKpi label="Sirenje marze" value={yoy.spread_expansion_bps != null ? `${yoy.spread_expansion_bps} bps` : "-"} tone={deltaTone(yoy.spread_expansion_bps)} />
                </div>
            )}
        </div>
    );
}

/* ── Hero KPI card with sparkline ───────────────────────────────────────── */
function HeroKpi({ label, value, sub, delta: deltaText, deltaToneColor, spark, accent = false }: {
    label: string; value: string; sub?: string; delta?: string; deltaToneColor?: string; spark?: number[]; accent?: boolean;
}) {
    return (
        <div style={{ border: `1px solid ${accent ? ACC : BORDER}`, backgroundColor: BG, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: GRAYS[2], textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: accent ? ACC : INK, fontVariantNumeric: "tabular-nums" }}>{value}</div>
                {spark && spark.length > 2 && (
                    <div style={{ flex: 1, maxWidth: 90, height: 28 }}>
                        <Sparkline values={spark} width={90} height={28} color={accent ? ACC : GRAYS[3]} />
                    </div>
                )}
            </div>
            {sub && <div style={{ fontSize: 11, color: GRAYS[1] }}>{sub}</div>}
            {deltaText && <div style={{ fontSize: 11, fontWeight: 700, color: deltaToneColor ?? GRAYS[2] }}>{deltaText}</div>}
        </div>
    );
}

/* ── Mini KPI card ──────────────────────────────────────────────────────── */
function MiniKpi({ label, value, tone }: { label: string; value: string; tone?: string }) {
    return (
        <div style={{ border: `1px solid ${BORDER}`, padding: "10px 14px", backgroundColor: BG_SUBTLE }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: GRAYS[2], textTransform: "uppercase" }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: tone ?? INK, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{value}</div>
        </div>
    );
}

function renderPnlView(data: any) {
    if (!data) return <p>Ucitavanje P&L podataka...</p>;
    const acc = data.accounting;
    const eco = data.economic;
    const rec = data.reconciliation;
    // OPEX grupe konta (isto kao Uprava P&L): knjig i ekon rezim kocke, spojeno po grupi
    const opexMap = new Map<string, { code: string; name: string; konta: string[]; k: number; e: number }>();
    for (const g of acc.opex?.groups ?? []) opexMap.set(g.code, { code: g.code, name: g.name, konta: g.konta ?? [], k: g.amount_eur ?? 0, e: 0 });
    for (const g of eco.opex_groups ?? []) {
        const r = opexMap.get(g.code) ?? { code: g.code, name: g.name, konta: g.konta ?? [], k: 0, e: 0 };
        r.e = g.amount_eur ?? 0;
        opexMap.set(g.code, r);
    }
    const opexRows = [...opexMap.values()].sort((a, b) => b.k - a.k);

    return (
        <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
                <KpiCard title="KONSOLIDIRANI PRIHOD" val={eur(acc.totals.revenue_eur)} sub={`Investicijsko: ${eur(acc.ado.revenue_eur)} | Lom: ${eur(acc.lom.revenue_eur)}`} />
                <KpiCard title="BRUTO MARŽA (KNJIGOVODSTVENO)" val={eur(acc.totals.gross_profit_eur)} sub={`Marža: ${pct(acc.totals.gross_margin_pct)} (COGS: ${eur(acc.totals.cogs_eur)})`} />
                <KpiCard title="OPERATIVNA DOBIT (EBITDA)" val={eur(acc.ebitda.operating_profit_eur)} sub={`EBITDA marža: ${pct(acc.ebitda.operating_margin_pct)} | OPEX: ${eur(acc.opex.total_opex_eur)}`} />
                <KpiCard title="EKONOMSKA BRUTO MARŽA" val={eur(eco.totals.economic_gross_profit_eur)} sub={`Marža: ${pct(eco.totals.economic_margin_pct)} | EBITDA: ${eur(eco.totals.economic_operating_profit_eur)}`} accent />
            </div>

            <div style={{ border: `1px solid ${BORDER}`, marginBottom: 24 }}>
                <div style={{ backgroundColor: BG_SUBTLE, padding: "10px 16px", borderBottom: `1px solid ${BORDER}`, fontWeight: 700, fontSize: 13, display: "flex", justifyContent: "space-between" }}>
                    <span>USPOREDNI PRIKAZ: KNJIGOVODSTVENI VS EKONOMSKI P&L</span>
                    <span style={{ color: ACC, fontWeight: 700 }}>Bruto knjig. - ekon.: {eur(rec.holding_gain_eur)}</span>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                        <tr style={{ backgroundColor: "#ffffff", borderBottom: `2px solid ${INK}` }}>
                            <th style={{ textAlign: "left", padding: "8px 12px" }}>POZICIJA RAČUNA DOBITI I GUBITKA</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", width: 170 }}>KNJIGOVODSTVENO (EUR)</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", width: 120 }}>UDIO (%)</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", width: 170, color: ACC }}>EKONOMSKI (EUR)</th>
                            <th style={{ textAlign: "left", padding: "8px 12px", width: 300 }}>METODOLOŠKO RAZGRANIČENJE</th>
                        </tr>
                    </thead>
                    <tbody>
                        <Row title="1. Prodaja investicijskog zlata (ADO)" v1={acc.ado.revenue_eur} v2={eco.ado.revenue_eur} p={pct(100)} note="Knjig.: računi. Ekon.: prihvaćena ponuda (dan ponude)" />
                        <Row title="2. Otkup i prerada loma zlata (TCA)" v1={acc.lom.revenue_eur} v2={eco.lom.revenue_eur} p={pct(100)} note="Ekon.: lom ADMS, prodaja TCA" />
                        <Row title="UKUPNI POSLOVNI PRIHOD" v1={acc.totals.revenue_eur} v2={eco.totals.revenue_eur} p={pct(100)} note="Konsolidirani prihodi od prodaje" bold bg />
                        <Row title="3. Trošak nabave investicijskog zlata (COGS)" v1={-acc.ado.cogs_eur} v2={-eco.ado.spot_cogs_eur} p={pct(acc.ado.revenue_eur > 0 ? (acc.ado.cogs_eur / acc.ado.revenue_eur)*100 : 0)} note="Knjig.: prosjek nabave po otpremnici. Ekon.: po ponudi (fixing 206, hedge StoneX, BeSoft)" />
                        <Row title="4. Trošak loma zlata (COGS)" v1={acc.lom.cogs_booked === false ? "nije knjiženo" : -acc.lom.cogs_eur} v2={-eco.lom.buyback_cogs_eur} p={pct(acc.lom.revenue_eur > 0 ? (acc.lom.cogs_eur / acc.lom.revenue_eur)*100 : 0)} note={acc.lom.cogs_booked === false ? "Knjig.: Faros ADMS nije knjižio 710. Ekon.: FIFO otkup prema prodaji TCA" : "Knjig.: prosjek nabave. Ekon.: FIFO otkup loma prema prodaji TCA"} />
                        <Row title="UKUPAN TROŠAK ROBE (COGS)" v1={-acc.totals.cogs_eur} v2={-(eco.totals.cogs_eur ?? 0)} p={pct((acc.totals.cogs_eur / acc.totals.revenue_eur)*100)} note="Ukupni trošak prodane robe" bold bg />
                        <Row title="BRUTO MARŽA (GROSS PROFIT)" v1={acc.totals.gross_profit_eur} v2={eco.totals.economic_gross_profit_eur} p={pct(acc.totals.gross_margin_pct)} note="Bruto profitabilnost prije operativnih troškova" bold highlight />
                        {opexRows.map((g, i) => (
                            <Row key={g.code} title={`${5 + i}. ${g.name}`} v1={-g.k} v2={-g.e} p={pct(acc.totals.revenue_eur ? (g.k / acc.totals.revenue_eur) * 100 : 0)} note={`Konta ${g.konta.join(", ")}`} />
                        ))}
                        <Row title="Ostale djelatnosti ADO i ADMS (zalog, mjenjačnica, ostali prihodi)" v1={(acc.ostalo?.gross_profit_eur ?? 0) + (acc.ostalo?.prihod_ostali_eur ?? 0)} v2={(eco.ostalo?.economic_margin_eur ?? 0) + (eco.ostalo?.prihod_ostali_eur ?? 0)} p="-" note="Bruto ostalih djelatnosti i ostali prihodi iz kocke" />
                        <Row title="StoneX (hedge rezultat izvan ponuda)" v1="-" v2={eco.stonex_eur ?? 0} p="-" note="PMX MT0811: mjesečni rezultat umanjen za hedge pripisan ponudama" />
                        <Row title="UKUPNI OPERATIVNI TROŠKOVI (OPEX)" v1={-acc.opex.total_opex_eur} v2={-(eco.opex_eur ?? 0)} p={pct(acc.opex.opex_to_revenue_pct)} note="Svi operativni troškovi poslovanja" bold bg />
                        <Row title="OPERATIVNA DOBIT (EBITDA)" v1={acc.ebitda.operating_profit_eur} v2={eco.totals.economic_operating_profit_eur} p={pct(acc.ebitda.operating_margin_pct)} note="Dobit prije amortizacije, kamata i poreza" bold highlight />
                        <Row title="13. Kapitalne investicije (CAPEX)" v1={-acc.capex.total_capex_eur} v2={-(eco.capex_eur ?? 0)} p="-" note="Preuređenja poslovnica, protuprovalni sefovi, IT oprema" />
                        <Row title="NETO REZULTAT NAKON CAPEX-a" v1={acc.net_after_capex_eur} v2={eco.totals.economic_operating_profit_eur - (eco.capex_eur ?? 0)} p="-" note="Slobodni novčani tijek nakon investicijskih ulaganja" bold bg />
                    </tbody>
                </table>
            </div>

            <div style={{ border: `1px solid ${INK}`, backgroundColor: BG_SUBTLE, padding: "12px 16px", fontSize: 12, lineHeight: 1.6 }}>
                <div><strong>Knjigovodstveni stupac:</strong> {(acc.notes ?? []).join(" ")}</div>
                <div style={{ marginTop: 6 }}>
                    <strong>Ekonomski stupac:</strong> {(eco.notes ?? []).join(" ")}
                    {eco.period ? <> Razdoblje: {eco.period.from} do {eco.period.to}.</> : null}
                    {eco.build?.built_at ? <> Kocka izgrađena {eco.build.built_at}.</> : null}
                </div>
            </div>
        </div>
    );
}

function Row({ title, v1, v2, p, note, bold = false, bg = false, highlight = false }: any) {
    return (
        <tr style={{
            borderBottom: `1px solid ${BORDER}`,
            backgroundColor: highlight ? "#edf2f7" : (bg ? BG_SUBTLE : "#ffffff"),
            fontWeight: bold ? 700 : 400,
        }}>
            <td style={{ padding: "8px 12px" }}>{title}</td>
            <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {typeof v1 === "number" ? eur(v1) : v1}
            </td>
            <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {p}
            </td>
            <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: ACC, fontWeight: bold ? 700 : 600 }}>
                {typeof v2 === "number" ? eur(v2) : v2}
            </td>
            <td style={{ padding: "8px 12px", color: "#4a5568", fontSize: 11 }}>{note}</td>
        </tr>
    );
}

function renderCostsView(data: any, costType: string, setCostType: (s: string) => void, drillCat: string, setDrillCat: (s: string) => void, drillClass: string, setDrillClass: (s: string) => void, drilldownData: any, drillLoading: boolean) {
    if (!data) return <p>Ucitavanje troskova...</p>;
    const sum = data.summary;
    const cats = data.categories ?? [];
    const top = data.top_suppliers ?? [];
    const drillRows = drilldownData?.rows ?? [];

    /* Breadcrumb for hierarchical drilldown */
    const hasDrill = drillCat !== "" || drillClass !== "";
    const drillCatLabel = (drillCat && cats.find((c: any) => c.code === drillCat)?.name) || drillCat;

    return (
        <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
                <HeroKpi label="UKUPNI OPEX" value={eurK(sum?.total_opex_eur)} sub="Operativni troskovi poslovanja" />
                <HeroKpi label="OGLASAVANJE I MARKETING" value={eurK(sum?.advertising_total_eur)} sub="Konto 415 (Google, Meta), knjiženo na ADMS" accent />
                <HeroKpi label="MREZA POSLOVNICA" value={eurK(sum?.store_network_total_eur)} sub="Svi troškovi na MTP-u poslovnica" />
                <HeroKpi label="UKUPNI CAPEX" value={eurK(sum?.total_capex_eur)} sub="Klasa 0 (oprema, uredenje, softver)" />
            </div>

            {/* Type filter buttons */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
                {["ALL", "OPEX", "CAPEX"].map((t) => (
                    <button key={t} onClick={() => { setCostType(t); setDrillCat(""); setDrillClass(""); }}
                        style={{ padding: "6px 14px", backgroundColor: costType === t ? INK : BG, color: costType === t ? BG : INK, border: `1px solid ${INK}`, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                        {t === "ALL" ? "SVI TROSKOVI" : t}
                    </button>
                ))}
                {hasDrill && (
                    <div style={{ marginLeft: 16, fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontWeight: 700, color: GRAYS[2] }}>DRILLDOWN:</span>
                        <button onClick={() => { setDrillCat(""); setDrillClass(""); }} style={{ ...btnOutline, fontSize: 11, padding: "2px 8px" }}>SVI</button>
                        {drillCat && <span style={{ color: ACC, fontWeight: 700 }}>{drillCatLabel}</span>}
                        {drillClass && <><span style={{ color: GRAYS[3] }}>/</span><span style={{ color: ACC, fontWeight: 700 }}>{drillClass}</span></>}
                    </div>
                )}
            </div>

            {/* Treemap visualization for cost categories */}
            {!hasDrill && cats.length > 0 && (
                <div style={{ border: `1px solid ${BORDER}`, padding: 16, marginBottom: 20, backgroundColor: BG }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>VIZUALIZACIJA TROSKOVA PO KATEGORIJAMA (kliknite za drilldown)</div>
                    <Treemap
                        items={cats.map((c: any, i: number) => ({ label: c.name ?? c.code, value: Math.abs(c.amount_eur ?? 0), color: GRAYS[Math.min(i, GRAYS.length - 1)] }))}
                        height={180}
                    />
                </div>
            )}

            {/* Category table - clickable for drilldown */}
            <div style={{ border: `1px solid ${BORDER}`, marginBottom: 20 }}>
                <div style={sectionTitle}>
                    {hasDrill ? `GL RETCI ZA: ${drillCatLabel}${drillClass ? " / " + drillClass : ""}` : "STRUKTURA PO GRUPAMA KONTA (KNJIGOVODSTVENO)"}
                </div>
                {!hasDrill ? (
                    <table style={tblStyle}>
                        <thead>
                            <tr style={{ backgroundColor: BG }}>
                                <th style={thStyle}>KATEGORIJA TROSKA</th>
                                <th style={{ ...thStyle, textAlign: "center", width: 90 }}>TIP</th>
                                <th style={{ ...thStyle, textAlign: "right", width: 140 }}>IZNOS (EUR)</th>
                                <th style={{ ...thStyle, textAlign: "right", width: 90 }}>UDIO (%)</th>
                                <th style={{ ...thStyle, width: 220 }}>MJESTO TROSKA (MTP)</th>
                                <th style={thStyle}>VODECI DOBAVLJAC</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cats.map((c: any) => (
                                <tr key={c.code} style={{ borderBottom: `1px solid ${BORDER}`, cursor: "pointer" }}
                                    onClick={() => setDrillCat(c.code ?? c.name)}>
                                    <td style={{ ...tdStyle, fontWeight: 600, color: ACC }}>{c.name}</td>
                                    <td style={{ ...tdStyle, textAlign: "center" }}>
                                        <span style={{ padding: "2px 6px", border: `1px solid ${INK}`, fontSize: 10, fontWeight: 700 }}>{c.type}</span>
                                    </td>
                                    <td style={{ ...tdNum, fontWeight: 700 }}>{eur(c.amount_eur)}</td>
                                    <td style={tdNum}>{pct(c.share_pct)}</td>
                                    <td style={{ ...tdStyle, color: GRAYS[1] }}>{c.mtp}</td>
                                    <td style={tdStyle}>{c.top_supplier}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    /* Drilldown: show individual invoices for selected category */
                    <table style={tblStyle}>
                        <thead>
                            <tr style={{ backgroundColor: BG }}>
                                <th style={{ ...thStyle, width: 95 }}>DATUM</th>
                                <th style={{ ...thStyle, width: 200 }}>DOBAVLJAC</th>
                                <th style={{ ...thStyle, width: 140 }}>DOKUMENT</th>
                                <th style={thStyle}>OPIS</th>
                                <th style={{ ...thStyle, width: 100 }}>KONTO</th>
                                <th style={{ ...thStyle, textAlign: "center", width: 90 }}>MTP</th>
                                <th style={{ ...thStyle, textAlign: "right", width: 120 }}>IZNOS (EUR)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {drillLoading ? (
                                <tr><td colSpan={7} style={{ ...tdStyle, textAlign: "center", color: ACC, fontWeight: 700 }}>UCITAVANJE RACUNA...</td></tr>
                            ) : drillRows.length === 0 ? (
                                <tr><td colSpan={7} style={{ ...tdStyle, textAlign: "center", color: GRAYS[2] }}>Nema zapisa za odabranu kategoriju.</td></tr>
                            ) : drillRows.map((r: any, idx: number) => (
                                <tr key={idx} style={{ borderBottom: `1px solid ${BORDER}` }}>
                                    <td style={{ ...tdStyle, color: GRAYS[2], fontVariantNumeric: "tabular-nums" }}>{r.date}</td>
                                    <td style={{ ...tdStyle, fontWeight: 700 }}>{r.partner}</td>
                                    <td style={{ ...tdStyle, fontVariantNumeric: "tabular-nums" }}>{r.document}</td>
                                    <td style={{ ...tdStyle, color: GRAYS[0] }}>{r.description}</td>
                                    <td style={{ ...tdStyle, fontSize: 11, color: GRAYS[1] }}>{r.account_name}</td>
                                    <td style={{ ...tdStyle, textAlign: "center", fontSize: 11 }}>{r.mtp}</td>
                                    <td style={{ ...tdNum, fontWeight: 700 }}>{eur2(r.amount_eur)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Top suppliers table (only when not drilling) */}
            {!hasDrill && (
                <div style={{ border: `1px solid ${BORDER}` }}>
                    <div style={sectionTitle}>TOP 20 DOBAVLJACA PO IZNOSU I MTP ALOKACIJI</div>
                    <table style={tblStyle}>
                        <thead>
                            <tr style={{ backgroundColor: BG }}>
                                <th style={{ ...thStyle, textAlign: "center", width: 50 }}>R.BR.</th>
                                <th style={thStyle}>NAZIV DOBAVLJACA</th>
                                <th style={{ ...thStyle, width: 220 }}>KATEGORIJA</th>
                                <th style={{ ...thStyle, textAlign: "center", width: 90 }}>MTP</th>
                                <th style={{ ...thStyle, textAlign: "center", width: 90 }}>KONTO</th>
                                <th style={{ ...thStyle, textAlign: "right", width: 140 }}>UKUPNO (EUR)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {top.map((s: any) => (
                                <tr key={s.rank} style={{ borderBottom: `1px solid ${BORDER}` }}>
                                    <td style={{ ...tdStyle, textAlign: "center", color: GRAYS[2] }}>{s.rank}.</td>
                                    <td style={{ ...tdStyle, fontWeight: 700 }}>{s.partner}</td>
                                    <td style={{ ...tdStyle, color: GRAYS[1] }}>{s.category}</td>
                                    <td style={{ ...tdStyle, textAlign: "center" }}>{s.mtp}</td>
                                    <td style={{ ...tdStyle, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{s.konto}</td>
                                    <td style={{ ...tdNum, fontWeight: 700 }}>{eur(s.amount_eur)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function renderDrilldownView(data: any, searchQuery: string, setSearchQuery: (s: string) => void, drillLoading: boolean) {
    if (!data && !drillLoading) return <p>Ucitavanje drilldown stavki...</p>;
    const rows = data?.rows ?? [];

    return (
        <div>
            <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center" }}>
                <input
                    type="text"
                    placeholder="Pretraži po partneru (Google, Meta, HEP...), dokumentu ili kontu..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ flex: 1, padding: "8px 12px", border: `1px solid ${BORDER}`, fontSize: 13 }}
                />
                <span style={{ fontSize: 12, color: "#718096" }}>
                    {drillLoading ? "Ucitavanje..." : `Prikazano: ${rows.length} zapisa`}
                </span>
            </div>

            <div style={{ border: `1px solid ${BORDER}` }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                        <tr style={{ backgroundColor: BG_SUBTLE, borderBottom: `2px solid ${INK}` }}>
                            <th style={{ textAlign: "left", padding: "8px 12px", width: 95 }}>DATUM</th>
                            <th style={{ textAlign: "left", padding: "8px 12px", width: 220 }}>DOBAVLJAČ / PARTNER</th>
                            <th style={{ textAlign: "left", padding: "8px 12px", width: 140 }}>BROJ DOKUMENTA</th>
                            <th style={{ textAlign: "left", padding: "8px 12px" }}>OPIS KNJIŽENJA</th>
                            <th style={{ textAlign: "left", padding: "8px 12px", width: 160 }}>KONTO NAZIV</th>
                            <th style={{ textAlign: "center", padding: "8px 12px", width: 130 }}>MTP LOKACIJA</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", width: 120 }}>IZNOS (EUR)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r: any, idx: number) => (
                            <tr key={idx} style={{ borderBottom: `1px solid ${BORDER}` }}>
                                <td style={{ padding: "8px 12px", fontVariantNumeric: "tabular-nums", color: "#718096" }}>{r.date}</td>
                                <td style={{ padding: "8px 12px", fontWeight: 700 }}>{r.partner}</td>
                                <td style={{ padding: "8px 12px", fontVariantNumeric: "tabular-nums" }}>{r.document}</td>
                                <td style={{ padding: "8px 12px", color: "#2d3748" }}>{r.description}</td>
                                <td style={{ padding: "8px 12px", color: "#4a5568", fontSize: 11 }}>{r.account_name}</td>
                                <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 11 }}>{r.mtp}</td>
                                <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>
                                    {eur2(r.amount_eur)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function renderBranchesView(data: any, sortBy: string, sortDir: "asc" | "desc", setSortBy: (s: string) => void, setSortDir: (d: any) => void) {
    if (!data) return <p>Ucitavanje poslovnica...</p>;
    const branches = data.branches ?? [];
    const totals = data.network_totals ?? {};

    const handleSort = (col: string) => {
        if (sortBy === col) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(col);
            setSortDir("desc");
        }
    };

    /* Top branches for bar chart */
    const topByRevenue = [...branches].sort((a: any, b: any) => (b.revenue_eur ?? 0) - (a.revenue_eur ?? 0)).slice(0, 15);
    const topByMargin = [...branches].sort((a: any, b: any) => (b.net_contribution_eur ?? 0) - (a.net_contribution_eur ?? 0)).slice(0, 15);

    return (
        <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
                <HeroKpi label="UKUPNA PRODAJA MREZE" value={eurK(totals.revenue_eur)} sub={`Broj poslovnica: ${branches.length}`} />
                <HeroKpi label="BRUTO MARZA MREZE" value={eurK(totals.gross_profit_eur)} sub={`Transakcija: ${num(totals.tx_count)}`} />
                <HeroKpi label="TROSKOVI NAJMA I REZIJA" value={eurK(totals.store_costs_eur)} sub="Alocirani fiksni troskovi lokacija" />
                <HeroKpi label="NETO DOPRINOS MREZE" value={eurK(totals.net_contribution_eur)} sub="Marza umanjena za najmove i rezije" accent />
            </div>

            {/* Bar charts: revenue vs net contribution */}
            {branches.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                    <div style={{ border: `1px solid ${BORDER}`, padding: 16, backgroundColor: BG }}>
                        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>TOP 15 PO PRIHODU</div>
                        <RankedBar items={topByRevenue.map((b: any) => ({ label: b.name, value: b.revenue_eur ?? 0 }))} />
                    </div>
                    <div style={{ border: `1px solid ${BORDER}`, padding: 16, backgroundColor: BG }}>
                        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>TOP 15 PO NETO DOPRINOSU</div>
                        <RankedBar items={topByMargin.map((b: any) => ({ label: b.name, value: b.net_contribution_eur ?? 0 }))} />
                    </div>
                </div>
            )}

            <div style={{ border: `1px solid ${BORDER}` }}>
                <div style={{ ...sectionTitle, display: "flex", justifyContent: "space-between" }}>
                    <span>RANG LISTA POSLOVNICA (SORTIRANO PO: {sortBy.toUpperCase()} {sortDir.toUpperCase()})</span>
                    <span style={{ color: GRAYS[2], fontSize: 12, fontWeight: 400 }}>Kliknite na zaglavlje stupca za sortiranje</span>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                        <tr style={{ backgroundColor: "#ffffff", borderBottom: `2px solid ${INK}` }}>
                            <th style={{ textAlign: "center", padding: "8px 10px", width: 55, cursor: "pointer" }} onClick={() => handleSort("branch_code")}>KOD</th>
                            <th style={{ textAlign: "left", padding: "8px 12px", cursor: "pointer" }} onClick={() => handleSort("name")}>POSLOVNICA</th>
                            <th style={{ textAlign: "center", padding: "8px 10px", width: 80, cursor: "pointer" }} onClick={() => handleSort("region")}>REGIJA</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", width: 130, cursor: "pointer" }} onClick={() => handleSort("revenue_eur")}>PRODAJA (EUR)</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", width: 130, cursor: "pointer" }} onClick={() => handleSort("cogs_eur")}>NABAVA (EUR)</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", width: 120, cursor: "pointer" }} onClick={() => handleSort("gross_profit_eur")}>MARŽA (EUR)</th>
                            <th style={{ textAlign: "right", padding: "8px 10px", width: 75, cursor: "pointer" }} onClick={() => handleSort("gross_margin_pct")}>MARŽA %</th>
                            <th style={{ textAlign: "right", padding: "8px 10px", width: 80, cursor: "pointer" }} onClick={() => handleSort("tx_count")}>RAČUNI</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", width: 110, cursor: "pointer" }} onClick={() => handleSort("store_costs_eur")}>TROŠAK LOK.</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", width: 130, cursor: "pointer", color: ACC }} onClick={() => handleSort("net_contribution_eur")}>NETO DOPRINOS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {branches.map((b: any) => (
                            <tr key={b.branch_code} style={{ borderBottom: `1px solid ${BORDER}` }}>
                                <td style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700 }}>{b.dept_code ?? (b.mtp != null ? `OJ ${b.mtp}` : "-")}</td>
                                <td style={{ padding: "8px 12px", fontWeight: 600 }}>
                                    {b.name}
                                    <span style={{ display: "block", fontSize: 10, color: "#718096" }}>{b.region_name}</span>
                                </td>
                                <td style={{ padding: "8px 10px", textAlign: "center" }}>
                                    <span style={{ display: "inline-block", padding: "2px 5px", fontSize: 10, fontWeight: 700, border: `1px solid ${BORDER}` }}>
                                        {b.region}
                                    </span>
                                </td>
                                <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{eur(b.revenue_eur)}</td>
                                <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#4a5568" }}>{eur(b.cogs_eur)}</td>
                                <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{eur(b.gross_profit_eur)}</td>
                                <td style={{ padding: "8px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{pct(b.gross_margin_pct)}</td>
                                <td style={{ padding: "8px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{num(b.tx_count)}</td>
                                <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#718096" }}>{eur(b.store_costs_eur)}</td>
                                <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: ACC }}>{eur(b.net_contribution_eur)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function renderRegionsView(data: any) {
    if (!data) return <p>Ucitavanje regionalnih podataka...</p>;
    const regions = data.regions ?? [];

    return (
        <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, marginBottom: 24 }}>
                {regions.map((r: any) => (
                    <div key={r.code} style={{ border: `1px solid ${BORDER}`, padding: 16, backgroundColor: BG_SUBTLE }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#718096", textTransform: "uppercase" }}>{r.code}</div>
                        <div style={{ fontSize: 15, fontWeight: 700, margin: "4px 0", color: INK }}>{r.name}</div>
                        <div style={{ fontSize: 11, color: "#4a5568", marginBottom: 12 }}>{r.branch_count} poslovnica</div>
                        <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 8, display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <span>Poslovnica:</span>
                                <strong>{r.branch_count}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <span>Prodaja:</span>
                                <strong>{eur(r.revenue_eur)}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <span>Bruto marža:</span>
                                <strong>{eur(r.gross_profit_eur)}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <span>Prosj. marža:</span>
                                <strong>{pct(r.avg_margin_pct)}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", color: ACC, borderTop: `1px solid ${BORDER}`, paddingTop: 4 }}>
                                <span>Neto doprinos:</span>
                                <strong>{eur(r.net_contribution_eur)}</strong>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div style={{ border: `1px solid ${BORDER}` }}>
                <div style={{ backgroundColor: BG_SUBTLE, padding: "10px 16px", borderBottom: `1px solid ${BORDER}`, fontWeight: 700, fontSize: 13 }}>
                    PREGLED I KONTROLA PO REGIJAMA
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                        <tr style={{ backgroundColor: "#ffffff", borderBottom: `2px solid ${INK}` }}>
                            <th style={{ textAlign: "left", padding: "8px 12px" }}>REGIJA</th>
                            <th style={{ textAlign: "left", padding: "8px 12px" }}>BROJ POSLOVNICA</th>
                            <th style={{ textAlign: "center", padding: "8px 12px", width: 90 }}>LOKACIJA</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", width: 150 }}>PRODAJA (EUR)</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", width: 140 }}>NABAVA (EUR)</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", width: 130 }}>BRUTO MARŽA (EUR)</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", width: 100 }}>MARŽA %</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", width: 140 }}>TROŠKOVI NAJMA/REŽ.</th>
                            <th style={{ textAlign: "right", padding: "8px 12px", width: 150, color: ACC }}>NETO DOPRINOS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {regions.map((r: any) => (
                            <tr key={r.code} style={{ borderBottom: `1px solid ${BORDER}` }}>
                                <td style={{ padding: "8px 12px", fontWeight: 700 }}>{r.name}</td>
                                <td style={{ padding: "8px 12px", color: "#2d3748" }}>{r.branch_count}</td>
                                <td style={{ padding: "8px 12px", textAlign: "center" }}>{r.branch_count}</td>
                                <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{eur(r.revenue_eur)}</td>
                                <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#4a5568" }}>{eur(r.cogs_eur)}</td>
                                <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{eur(r.gross_profit_eur)}</td>
                                <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{pct(r.avg_margin_pct)}</td>
                                <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#718096" }}>{eur(r.store_costs_eur)}</td>
                                <td style={{ padding: "8px 12px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: ACC }}>{eur(r.net_contribution_eur)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function renderTrendsView(data: any) {
    if (!data) return <p>Ucitavanje trendova...</p>;
    const monthly = data.monthly ?? [];
    const yoy = data.yoy_q1 ?? {};
    const growth = yoy.growth;

    /* Chart data */
    const labels = monthly.map((m: any) => m.period);
    const revValues = monthly.map((m: any) => m.revenue_eur ?? 0);
    const marginValues = monthly.map((m: any) => m.gross_profit_eur ?? 0);
    const opexValues = monthly.map((m: any) => m.opex_eur ?? 0);
    const netValues = monthly.map((m: any) => m.net_profit_eur ?? null);
    const txValues = monthly.map((m: any) => m.tx_count ?? 0);

    return (
        <div>
            {/* YoY KPI row */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
                <HeroKpi label="RAST PRODAJE (YOY)" value={growth ? delta(growth.revenue_growth_pct) : "-"} spark={revValues} />
                <HeroKpi label="RAST BRUTO MARZE (YOY)" value={growth ? delta(growth.gross_profit_growth_pct) : "-"} spark={marginValues} accent />
                <HeroKpi label="RAST BROJA RACUNA" value={growth ? delta(growth.tx_growth_pct) : "-"} spark={txValues} />
                <HeroKpi label="SIRENJE MARZE" value={growth?.spread_expansion_bps != null ? `${growth.spread_expansion_bps} bps` : "-"} />
            </div>

            {/* Multi-line chart: revenue + margin + net */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                <div style={{ border: `1px solid ${BORDER}`, padding: 16, backgroundColor: BG }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>TREND PRIHODA, MARZE I EBITDA (EKONOMSKI)</div>
                    <NiceLineChart
                        series={[
                            { name: "Prihod", color: INK, values: revValues },
                            { name: "Bruto marza", color: ACC, values: marginValues },
                            { name: "Neto dobit", color: GRAYS[2], values: netValues, style: "dashed" },
                        ]}
                        xLabels={labels}
                        height={240}
                    />
                    <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 11, color: GRAYS[1] }}>
                        <span><span style={{ display: "inline-block", width: 14, height: 3, backgroundColor: INK, marginRight: 4, verticalAlign: "middle" }} /> Prihod</span>
                        <span><span style={{ display: "inline-block", width: 14, height: 3, backgroundColor: ACC, marginRight: 4, verticalAlign: "middle" }} /> Bruto marza</span>
                        <span><span style={{ display: "inline-block", width: 14, height: 3, backgroundColor: GRAYS[2], marginRight: 4, verticalAlign: "middle", borderTop: "1px dashed" + GRAYS[2] }} /> Neto</span>
                    </div>
                </div>
                <div style={{ border: `1px solid ${BORDER}`, padding: 16, backgroundColor: BG }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>OPEX TREND</div>
                    <NiceLineChart
                        series={[
                            { name: "OPEX", color: NEG, values: opexValues },
                            { name: "Prihod", color: GRAYS[3], values: revValues, style: "dashed" },
                        ]}
                        xLabels={labels}
                        height={240}
                    />
                </div>
            </div>

            {/* Monthly data table */}
            <div style={{ border: `1px solid ${BORDER}` }}>
                <div style={sectionTitle}>MJESECNI PODACI PO RAZDOBLJIMA</div>
                <table style={tblStyle}>
                    <thead>
                        <tr style={{ backgroundColor: BG }}>
                            <th style={thStyle}>RAZDOBLJE</th>
                            <th style={{ ...thStyle, textAlign: "right", width: 140 }}>PRODAJA (EUR)</th>
                            <th style={{ ...thStyle, textAlign: "right", width: 140 }}>NABAVA (EUR)</th>
                            <th style={{ ...thStyle, textAlign: "right", width: 130 }}>BRUTO MARZA</th>
                            <th style={{ ...thStyle, textAlign: "right", width: 80 }}>MARZA %</th>
                            <th style={{ ...thStyle, textAlign: "right", width: 120 }}>OPEX (EUR)</th>
                            <th style={{ ...thStyle, textAlign: "right", width: 130, color: ACC }}>NETO DOBIT</th>
                            <th style={{ ...thStyle, textAlign: "right", width: 85 }}>TRANSAKCIJE</th>
                        </tr>
                    </thead>
                    <tbody>
                        {monthly.map((m: any) => (
                            <tr key={m.period} style={{ borderBottom: `1px solid ${BORDER}`, backgroundColor: m.year === thisYear ? BG_SUBTLE : BG }}>
                                <td style={{ ...tdStyle, fontWeight: 700 }}>{m.period}</td>
                                <td style={{ ...tdNum, fontWeight: 700 }}>{eur(m.revenue_eur)}</td>
                                <td style={{ ...tdNum, color: GRAYS[1] }}>{eur(m.cogs_eur)}</td>
                                <td style={{ ...tdNum, fontWeight: 700 }}>{eur(m.gross_profit_eur)}</td>
                                <td style={tdNum}>{pct(m.gross_margin_pct)}</td>
                                <td style={{ ...tdNum, color: GRAYS[2] }}>{eur(m.opex_eur)}</td>
                                <td style={{ ...tdNum, fontWeight: 700, color: ACC }}>{eur(m.net_profit_eur)}</td>
                                <td style={tdNum}>{num(m.tx_count)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* KpiCard kept for backward compat - delegates to HeroKpi */
function KpiCard({ title, val, sub, accent = false }: { title: string; val: string; sub?: string; accent?: boolean }) {
    return <HeroKpi label={title} value={val} sub={sub} accent={accent} />;
}
